import {
  Anchor,
  Box,
  Button,
  Divider,
  MantineProvider,
  Select,
  type SelectItem,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import {useForm, zodResolver} from '@mantine/form';
import {useDisclosure} from '@mantine/hooks';
import {Notifications} from '@mantine/notifications';
import type {AppError} from '@packages/utils';
import Head from 'next/head';
import {useRouter} from 'next/router';
import type React from 'react';
import {useMemo} from 'react';
import {routes} from '../../../config/constants';
import {COLOR_LINK_1} from '../../../config/constants/colorCodes';
import {GSSKT_HOST} from '../../../config/constants/hosts';
import {isReleasedNewRegisterFlow} from '../../../config/unReleasePage';
import {PostInquiryGeneralSchema} from '../../../core/entities/inquiry/schema';
import {
  type PostInquiryGeneralDataType,
  usePostInquiryGeneral,
} from '../../../core/repositories/inquiryRepository';
import {useReCaptcha} from '../../../core/usecases/useReCaptcha';
import {CommonLink} from '../../../shared/components/CommonLink';
import {
  showErrorSnackBar,
  showSuccessSnackBar,
} from '../../../shared/components/SnackBar';
import {useIsAppWebView} from '../../../shared/hooks/useIsAppWebView';

type Props = Record<string, never>;

type CastedError = {message: AppError};

const VALUE_SUBJECT_QUIT = '退会について';
const createSubjectOptions = (isNewRegisterFlow: boolean): SelectItem[] => {
  const studentCardSubject = isNewRegisterFlow
    ? '学生証による大学認証について'
    : '学生証審査について';

  return [
    {value: '-', label: '---'},
    {value: 'サイトの利用方法について', label: 'サイトの利用方法について'},
    {
      value: 'エントリーしている選考／イベントについて',
      label: 'エントリーしている選考／イベントについて',
    },
    {value: 'プロフィールの変更について', label: 'プロフィールの変更について'},
    {value: 'その他のお問い合わせ', label: 'その他のお問い合わせ'},
    {value: VALUE_SUBJECT_QUIT, label: '退会について'},
    {value: studentCardSubject, label: studentCardSubject},
    {
      value: '投稿したES・体験記の審査・報酬について',
      label: '投稿したES・体験記の審査・報酬について',
    },
    {value: '外資就活相談室について', label: '外資就活相談室について'},
  ];
};

export const PostInquiryGeneral: React.FC<Props> = () => {
  const isWebView = useIsAppWebView();
  const router = useRouter();
  const currentUrl = `${GSSKT_HOST}${decodeURI(router.pathname)}`;
  const {reCaptcha} = useReCaptcha();
  const subjectOptions = createSubjectOptions(isReleasedNewRegisterFlow());

  const form = useForm({
    validate: zodResolver(PostInquiryGeneralSchema),
    initialValues: {
      body: '',
      email: '',
      name: '',
      subject: '-',
    },
  });
  const saveInquiryGeneral = usePostInquiryGeneral({
    onSuccessFnc() {
      handlers.close();
      showSuccessSnackBar({title: '送信しました', message: 'done'});
    },
    onErrorFnc(error: unknown) {
      const castedError = error as CastedError;
      handlers.close();
      showErrorSnackBar({
        isNetworkError: castedError.message === 'NetworkError',
      });
    },
  });
  const [isLoading, handlers] = useDisclosure(false);

  const submitPost = (values: PostInquiryGeneralDataType) => {
    if (isWebView) {
      saveInquiryGeneral.mutate(
        values && {
          ...values,
        },
      );
    } else {
      reCaptcha('submit')
        .then(() => {
          handlers.open();
          saveInquiryGeneral.mutate(
            values && {
              ...values,
            },
          );
        })
        .catch(() => {
          showErrorSnackBar({
            isNetworkError: false,
            title: 'reCAPTCHAエラー',
            message: 'reCAPTCHAエラー',
          });
        });
    }
  };

  const isShowQuitLink = useMemo(() => {
    return form.values.subject === VALUE_SUBJECT_QUIT;
  }, [form.values.subject]);

  return (
    <>
      <Head>
        <title key="title">お問い合わせ</title>
        <meta key="og:title" property="og:title" content="お問い合わせ" />
        <link rel="canonical" href={currentUrl} key="canonical" />
      </Head>

      <Box className="flex min-h-screen w-full max-w-[664px] flex-col items-center bg-background-1">
        <MantineProvider>
          <Notifications position={'top-right'} />
          <Box className="w-full max-w-[664px]">
            <Box
              sx={() => ({
                marginTop: '10px',
              })}
            >
              <Title
                order={1}
                className="border-t-4 border-primary-1 bg-white p-16 text-lg text-primary-1 pc:px-32"
              >
                お問い合わせ
              </Title>
            </Box>
            <Box
              className=" bg-white p-16 pc:px-32"
              sx={() => ({
                marginTop: '10px',
                marginBottom: '20px',
              })}
            >
              <Box className="py-16">
                <Text
                  sx={() => ({
                    fontSize: '14px',
                  })}
                >
                  お問い合わせの前にヘルプの
                  <Anchor className="text-link" href="/faq">
                    よくある質問
                  </Anchor>
                  をご確認ください。
                </Text>
                <Text
                  sx={() => ({
                    fontSize: '12px',
                    marginTop: '16px',
                    color: 'red',
                  })}
                />
              </Box>
              <form
                onSubmit={form.onSubmit((values) => {
                  submitPost(values);
                })}
              >
                <Select
                  withAsterisk
                  label="件名"
                  placeholder="選択してください（必須）"
                  data={subjectOptions}
                  {...form.getInputProps('subject')}
                  sx={() => ({
                    marginBottom: '1rem',
                  })}
                />
                {isShowQuitLink ? (
                  <Box className="mt-24">
                    <Text className="text-md">
                      退会ページは
                      <CommonLink href={routes.QUIT} className="text-link">
                        こちら
                      </CommonLink>
                    </Text>
                  </Box>
                ) : (
                  <Box>
                    <TextInput
                      label="お名前"
                      placeholder="お名前"
                      withAsterisk={true}
                      {...form.getInputProps('name')}
                      sx={() => ({
                        marginBottom: '1rem',
                      })}
                    />
                    <TextInput
                      label="メールアドレス"
                      placeholder="メールアドレス"
                      withAsterisk={true}
                      {...form.getInputProps('email')}
                      sx={() => ({
                        marginBottom: '1rem',
                      })}
                    />
                    <Textarea
                      placeholder="お問い合わせ内容"
                      label="お問い合わせ内容"
                      withAsterisk={true}
                      {...form.getInputProps('body')}
                      sx={() => ({
                        marginBottom: '1rem',
                      })}
                    />
                    <Divider my="lg" />
                    <Box className="px-8 py-16 pc:px-32">
                      <Text
                        sx={() => ({
                          fontSize: '14px',
                        })}
                      >
                        個人情報の利用について、
                        <CommonLink href="/pmark" className="text-link">
                          「個人情報の取扱い」
                        </CommonLink>
                        を必ずご確認ください。
                      </Text>
                    </Box>
                    <Box className="px-8 py-16 pc:px-32">
                      <Button
                        fullWidth
                        variant="outline"
                        loading={isLoading}
                        type="submit"
                        styles={() => ({
                          label: {
                            color: COLOR_LINK_1,
                            fontWeight: 300,
                          },
                          root: {
                            borderColor: COLOR_LINK_1,
                          },
                        })}
                      >
                        「個人情報の取り扱い」に合意して送信
                      </Button>
                    </Box>
                  </Box>
                )}
              </form>
            </Box>
          </Box>
        </MantineProvider>
      </Box>
    </>
  );
};
